<script setup lang="ts">
import type { Ref } from "vue";
import { computed, ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  displayableEntities,
  filterableEntities,
} from "~/modules/log/constants";
import { LogEntityClass } from "~/modules/log/models/LogEntityClass.enum";
import type { LogEntityLink } from "~/modules/log/models/LogEntityLink.interface";
import ILxcCompass from "~icons/lxc/compass";
import ILxcFilter from "~icons/lxc/filter";
import ILxcTablet from "~icons/lxc/tablet";
import ILxcUser from "~icons/lxc/user";
import ILxcUsers from "~icons/lxc/users";

const { can } = useAcl();
const { t } = useI18n();

const router = useRouter();

const emit = defineEmits(["apply-as-a-filter"]);

const props = defineProps<{
  element?: HTMLLinkElement | null;
}>();
const entityLink: Ref<LogEntityLink | undefined | null> = ref();

const labelSeeEntityProperties = computed((): string => {
  return entityLink.value?.entityClass
    ? t(`logs.filters.dropdown.properties.${entityLink.value?.entityClass}`)
    : "";
});
const labelApplyEntityAsAFilter = computed((): string => {
  return entityLink.value?.entityClass
    ? t(`logs.filters.dropdown.applyAsAFilter`)
    : "";
});

const displayProperties = () => {
  if (entityLink.value?.href) {
    // remove the base URL because the href retrieved from the element is always absolute
    router.push(entityLink.value?.href.replace(document.location.origin, ""));
  }
};

const apply = () => {
  if (entityLink.value) {
    const { entityId, entityClass } = entityLink.value;
    emit("apply-as-a-filter", {
      entityId,
      entityClass,
    });
  }
};

const onCommand = (command: string) => {
  switch (command) {
    case "displayProperties":
      displayProperties();
      break;
    case "apply":
      apply();
      break;
  }
};

const isEntityDevice = computed((): boolean => {
  return props.element?.dataset?.entityclass === LogEntityClass.DEVICE;
});

const isEntityGroup = computed((): boolean => {
  return props.element?.dataset?.entityclass === LogEntityClass.GROUP;
});

const isEntityProfile = computed((): boolean => {
  return props.element?.dataset?.entityclass === LogEntityClass.PROFILE;
});

const isEntitySector = computed((): boolean => {
  return props.element?.dataset?.entityclass === LogEntityClass.SECTOR;
});

const isEntityUser = computed((): boolean => {
  return props.element?.dataset?.entityclass === LogEntityClass.USER;
});

const displayPropertiesEnabled = computed((): boolean => {
  let enabled = false;

  // If the action is delete and the link is an asset, hide the link to the user properties because it will display a 404 error
  if (
    props.element?.dataset?.property === "assetId" &&
    props.element?.dataset?.action === "delete"
  ) {
    return enabled;
  }

  switch (props.element?.dataset?.entityclass) {
    case LogEntityClass.DEVICE:
      enabled = can(ACLRoles.CAN_READ_DEVICE);
      break;
    case LogEntityClass.USER:
    case LogEntityClass.GROUP:
    case LogEntityClass.PROFILE:
      enabled = can(ACLRoles.CAN_READ_USER);
      break;
    case LogEntityClass.SECTOR:
      enabled = can(ACLRoles.CAN_READ_SECTOR);
      break;
    case LogEntityClass.PKI:
      enabled = can(ACLRoles.CAN_WRITE_FEATURE);
      break;
    case LogEntityClass.INTERFACE:
      enabled = can(ACLRoles.CAN_READ_APPLICATION);
      break;
    default:
      enabled = props.element?.dataset?.entityclass
        ? (displayableEntities[props.element.dataset.entityclass] ?? false)
        : false;
      break;
  }

  return enabled;
});

/**
 * TODO: to be refactored => Apply as a filter must be reworked
 */
const filterPropertiesEnabled = computed((): boolean => {
  return props.element?.dataset?.entityclass
    ? (filterableEntities[props.element.dataset.entityclass] ?? false)
    : false;
});

watch(
  () => props.element,
  (element?: HTMLLinkElement | null) => {
    if (element?.dataset?.entityclass && element?.dataset?.id) {
      entityLink.value = {
        entityClass: element.dataset.entityclass as LogEntityClass,
        href: element.href,
        entityId: element.dataset.id,
        property: element.dataset.property,
      };
    } else {
      entityLink.value = null;
    }
  },
);
</script>

<template>
  <lxc-dropdown-menu offset="8" :element="props.element" @command="onCommand">
    <template #default>
      <lxc-dropdown-item
        v-if="displayPropertiesEnabled"
        command="displayProperties"
        class="flex"
      >
        <i-lxc-tablet v-if="isEntityDevice" />
        <i-lxc-users v-else-if="isEntityGroup" />
        <i-lxc-user v-else-if="isEntityProfile || isEntityUser" />
        <i-lxc-compass v-else-if="isEntitySector" />
        <span class="pl-1 pt-1">
          {{ labelSeeEntityProperties }}
        </span>
      </lxc-dropdown-item>
      <lxc-dropdown-item
        v-if="filterPropertiesEnabled"
        command="apply"
        class="flex"
      >
        <i-lxc-filter />
        <span class="pl-1 pt-1">
          {{ labelApplyEntityAsAFilter }}
        </span>
      </lxc-dropdown-item>
    </template>
  </lxc-dropdown-menu>
</template>
